/** Deep links into CM-scoped logistics UIs (seller). */

/** Matches backend `CANONICAL_STAGE_ORDER` — used when `stages` array is missing but `progress` has keys. */
export const CM_CANONICAL_STAGE_ORDER = [
  "generate_invoice",
  "accept_invoice",
  "generate_eway_bill",
  "eway_bill_tracking",
  "generate_shipping_manifest",
  "accept_manifest",
  "generate_grn",
  "accept_grn",
];

export function sellerCmProductionPath(rfqId, stageId) {
  const id = encodeURIComponent(String(rfqId));
  const map = {
    generate_invoice: `/seller/cm-rfqs/${id}/invoice`,
    generate_eway_bill: `/seller/cm-rfqs/${id}/eway-bill`,
    eway_bill_tracking: `/seller/cm-rfqs/${id}/eway-tracking`,
    generate_shipping_manifest: `/seller/cm-rfqs/${id}/shipping-manifest`,
    accept_grn: `/seller/cm-rfqs/${id}/review-grn`,
  };
  return map[stageId] || null;
}

function normStageStatus(st) {
  return String(st ?? "")
    .trim()
    .toLowerCase();
}

/** Resolve progress row for a stage id (snake_case in API; tolerate camelCase keys). */
export function progressEntryForStage(progress, stageId) {
  const raw = stageId == null ? "" : typeof stageId === "object" && stageId !== null ? stageId.id ?? stageId.stageId ?? stageId.stage_id ?? "" : stageId;
  const k = String(raw ?? "").trim();
  if (!k || !progress || typeof progress !== "object" || Array.isArray(progress)) return undefined;
  if (Object.prototype.hasOwnProperty.call(progress, k)) return progress[k];
  const camel = k.replace(/_([a-z])/g, (_, ch) => String(ch).toUpperCase());
  if (camel !== k && Object.prototype.hasOwnProperty.call(progress, camel)) return progress[camel];
  const snake = k.includes("_")
    ? k
    : k.replace(/([a-z0-9])([A-Z])/g, "$1_$2").toLowerCase();
  if (snake !== k && Object.prototype.hasOwnProperty.call(progress, snake)) return progress[snake];
  return undefined;
}

function normalizeJsonObject(v) {
  if (v && typeof v === "object" && !Array.isArray(v)) return v;
  if (typeof v === "string" && v.trim()) {
    try {
      const p = JSON.parse(v);
      return p && typeof p === "object" && !Array.isArray(p) ? p : {};
    } catch {
      return {};
    }
  }
  return {};
}

function normalizeJsonArray(v) {
  if (Array.isArray(v)) return v;
  if (typeof v === "string" && v.trim()) {
    try {
      const p = JSON.parse(v);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return [];
}

/** Stable snake_case stage id for UI + lookups (handles objects / camelCase strings). */
export function normalizeCmStageId(sid) {
  const raw =
    sid == null
      ? ""
      : typeof sid === "object" && sid !== null
        ? sid.id ?? sid.stageId ?? sid.stage_id ?? ""
        : sid;
  let s = String(raw ?? "").trim();
  if (!s) return "";
  if (!s.includes("_")) {
    s = s.replace(/([a-z0-9])([A-Z])/g, "$1_$2").toLowerCase();
  }
  return s;
}

/**
 * Prefer API `stages` list; if missing/empty, derive ordered stages from `progress` keys
 * (fixes buyer workspace when `stages` is under another field or not deserialized).
 */
export function resolveCmProductionStages(stagesRaw, progressRaw) {
  const progress = normalizeJsonObject(progressRaw);
  const fromApi = normalizeJsonArray(stagesRaw).map((x) => normalizeCmStageId(x)).filter(Boolean);
  if (fromApi.length > 0) return fromApi;
  return CM_CANONICAL_STAGE_ORDER.filter((id) => progressEntryForStage(progress, id) != null);
}

/** `progress` map as object (parses JSON string envelopes from some API clients). */
export function normalizeCmProgressMap(progressRaw) {
  return normalizeJsonObject(progressRaw);
}

/** Treat stage as completed (API may vary casing; missing key is not completed). */
export function isCmStageProgressCompleted(progress, stageId) {
  if (!progress || typeof progress !== "object" || stageId == null) return false;
  const entry = progressEntryForStage(progress, stageId);
  return normStageStatus(entry?.status) === "completed";
}

/** True when every configured stage is completed (same rule as pipeline badges). */
export function isCmPipelineFullyComplete(stages, progress) {
  const list = Array.isArray(stages) ? stages : [];
  if (list.length === 0 || !progress || typeof progress !== "object") return false;
  return list.every((sid) => isCmStageProgressCompleted(progress, sid));
}

export function findCurrentProductionStageId(stages, progress) {
  if (!Array.isArray(stages) || !progress || typeof progress !== "object") return null;
  for (const sid of stages) {
    if (!isCmStageProgressCompleted(progress, sid)) return normalizeCmStageId(sid) || sid;
  }
  return null;
}

/** Stages the buyer completes in the CM production workspace (backend-aligned). */
export const CM_BUYER_LOGISTICS_STAGES = ["accept_invoice", "accept_manifest", "generate_grn"];

/**
 * True when every buyer-logistics stage that appears in this RFQ's pipeline is completed.
 * After the buyer finishes GRN, the pipeline often moves to seller-only `accept_grn`;
 * the buyer production workspace uses this for messaging until the full pipeline completes.
 *
 * @param {object} [opts]
 * @param {boolean} [opts.treatGenerateGrnCompleteIfGrn] — if true, `generate_grn` counts as done when a non-draft GRN
 *   exists on the CM fulfillment snapshot (covers buyers who recorded GRN but have not clicked "Mark step complete").
 */
export function areBuyerCmLogisticsStagesComplete(stages, progress, opts = {}) {
  if (!Array.isArray(stages) || stages.length === 0 || !progress || typeof progress !== "object") return false;
  const normStages = stages.map((s) => normalizeCmStageId(s)).filter(Boolean);
  const relevant = normStages.filter((sid) => CM_BUYER_LOGISTICS_STAGES.includes(sid));
  if (relevant.length === 0) return false;
  const { treatGenerateGrnCompleteIfGrn = false } = opts;
  return relevant.every((sid) => {
    if (sid === "generate_grn" && treatGenerateGrnCompleteIfGrn) {
      return isCmStageProgressCompleted(progress, sid) || treatGenerateGrnCompleteIfGrn;
    }
    return isCmStageProgressCompleted(progress, sid);
  });
}

/** Leading digits from route param (e.g. "1" from "1--") for stable API URLs. */
export function parseCmRfqIdParam(param) {
  const m = String(param ?? "").match(/^\d+/);
  return m ? m[0] : String(param ?? "").trim();
}

/** CommonService returns `{ success, message, data }` from the API envelope. */
export function unwrapApiData(apiPayload) {
  return apiPayload?.data ?? apiPayload;
}
